import { useState, useEffect, useCallback, useRef } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../../firebase';
import { normalizeMenuData, isLegacyShape, seedGriddleSections } from '../utils/dosaCombos';

const MENU_JSON_URL = '/menu-data.json';

/**
 * Last published menu doc seen on this device (the raw parsed doc, so the
 * current normalizer always runs on it). Lets a returning visitor see the menu
 * instantly instead of waiting on the Firestore round trip; the live copy
 * replaces it a moment later.
 */
const MENU_CACHE_KEY = 'db_menu_doc_v1';

function readCachedDoc() {
  try {
    const raw = localStorage.getItem(MENU_CACHE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    // A legacy-shape doc needs an async seed step — just wait for the live copy.
    return parsed && typeof parsed === 'object' && !isLegacyShape(parsed) ? parsed : null;
  } catch { return null; }
}

function writeCachedDoc(parsed) {
  try { localStorage.setItem(MENU_CACHE_KEY, JSON.stringify(parsed)); } catch { /* full / private mode */ }
}

function initialMenu() {
  const cached = readCachedDoc();
  if (!cached) return null;
  try { return normalizeMenuData(cached); } catch { return null; }
}

/**
 * Fetch and manage menu data: Firestore (admin overrides) → static JSON.
 *
 * Both sources are normalized to the flat shape; a doc still in an older shape
 * is additionally seeded from the shipped JSON, once (see dosaCombos.js).
 *
 * Returns { menuData, setMenuData, loading, error, refetch, stale }. `stale` is
 * true while the on-screen menu is this device's cached copy and the live one
 * hasn't landed yet — editing must wait for it, or an admin could publish over
 * a newer menu.
 */

const useMenuData = () => {
  const [menuData, setMenuData] = useState(initialMenu);
  const [loading, setLoading] = useState(() => menuData === null);
  const [error, setError] = useState(null);
  const [stale, setStale] = useState(() => menuData !== null);
  const hasMenu = useRef(menuData !== null);

  const fetchMenu = useCallback(async (signal) => {
    try {
      // With a cached menu on screen, refresh quietly behind it (no spinner).
      if (!hasMenu.current) setLoading(true);
      setError(null);

      // 1. Try Firestore first (contains admin-published changes)
      try {
        const snap = await getDoc(doc(db, 'menuConfig', 'main'));
        if (snap.exists()) {
          const raw = snap.data();
          const parsed = raw._json ? JSON.parse(raw._json) : raw;
          let data = normalizeMenuData(parsed);
          if (isLegacyShape(parsed)) {
            // Old docs stored only the griddle base rows, so seed those
            // sections; every other published section is left untouched.
            const seed = await fetch(MENU_JSON_URL, { signal })
              .then((r) => (r.ok ? r.json() : null))
              .catch(() => null);
            if (seed) data = seedGriddleSections(data, normalizeMenuData(seed));
          } else {
            writeCachedDoc(parsed);
          }
          if (!signal?.aborted) { setMenuData(data); hasMenu.current = true; setStale(false); }
          return;
        }
      } catch {
        // Firestore unavailable. A cached copy of the PUBLISHED menu beats the
        // shipped JSON (which lacks every in-app edit), so keep showing it.
        if (hasMenu.current) return;
      }

      // 2. Fall back to static JSON
      const res = await fetch(MENU_JSON_URL, { signal });
      if (!res.ok) throw new Error(`Failed to load menu (${res.status})`);
      const data = await res.json();
      if (!signal?.aborted) { setMenuData(normalizeMenuData(data)); hasMenu.current = true; setStale(false); }
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('Menu fetch error:', err);
        setError(err.message || 'Something went wrong');
      }
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    fetchMenu(controller.signal);
    return () => controller.abort();
  }, [fetchMenu]);

  const refetch = useCallback(() => fetchMenu(), [fetchMenu]);

  return { menuData, setMenuData, loading, error, refetch, stale };
};

export default useMenuData;
